import { useCallback } from 'react';
import type { NavigateOptions } from 'react-router-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { appRoutes } from '@/constants/routes';
import type { NoteVariant } from '@/providers/rxdb/constants';

type DocumentType = NoteVariant | 'userSources';

type OpenDraftParams<T extends DocumentType> = {
  type: T;
  prompt?: string;
  domain?: 'medical';
  navigateOptions?: NavigateOptions;
};

export const buildDraftUrl = ({
  type,
  domain,
  preserveSearchParams,
}: {
  type: DocumentType;
  domain?: 'medical';
  preserveSearchParams?: URLSearchParams;
}) => {
  const draftId = `new-${crypto.randomUUID()}`;
  const params = preserveSearchParams != null ? new URLSearchParams(preserveSearchParams) : new URLSearchParams();
  const searchParams = params.toString();

  if (type === 'userSources') {
    return searchParams ? `${appRoutes.cortex}/${draftId}?${searchParams}` : `${appRoutes.cortex}/${draftId}`;
  }

  params.set('draftType', type);
  if (domain) {
    params.set('domain', domain);
  } else {
    params.delete('domain');
  }
  const nextSearchParams = params.toString();
  return nextSearchParams ? `${appRoutes.library}/${draftId}?${nextSearchParams}` : `${appRoutes.library}/${draftId}`;
};

export const useDraftDocument = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const openDraft = useCallback(
    <T extends DocumentType>({ type, domain, navigateOptions }: OpenDraftParams<T>) => {
      const draftUrl = buildDraftUrl({ type, domain, preserveSearchParams: searchParams });
      if (navigateOptions) {
        navigate(draftUrl, navigateOptions);
        return;
      }
      navigate(draftUrl);
    },
    [navigate, searchParams]
  );

  return {
    openDraft,
  };
};
